// src/pages/PolicyInsightDashboard.tsx
// PolicyIntel modern policy-to-stock intelligence dashboard.
// 기존 useDashboardData / FeedDetailDialog 연결은 유지하고,
// 화면 목적을 "정책 기사 목록"에서 "정책 이슈가 종목 반응으로 전이되는지 검증하는 워크스페이스"로 재구성합니다.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';

import {
  Box,
  Button,
  Chip,
  Container,
  Divider,
  GlobalStyles,
  IconButton,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import CandlestickChartRoundedIcon from '@mui/icons-material/CandlestickChartRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded';
import PriceCheckRoundedIcon from '@mui/icons-material/PriceCheckRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SignalCellularAltRoundedIcon from '@mui/icons-material/SignalCellularAltRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';

import FeedDetailDialog from '../components/detail/FeedDetailDialog';
import {
  useDashboardData,
  type StockChange,
} from '../components/policy/dashboard/hooks/useDashboardData';

dayjs.locale('ko');

const NAV_H = 64;

type JudgmentLabel =
  | '우선 추적'
  | '관심 유지'
  | '수급 확인'
  | '눌림 대기'
  | '진입 경계'
  | '제외';

type ReactionDirection = 'up' | 'down' | 'flat';

type SummaryGrammar = {
  whatHappened: string;
  whyImportant: string;
  impactedTargets: string[];
  currentJudgment: JudgmentLabel;
  nextAction: string;
  riskLine: string;
};

type SignalFeed = any & {
  __ticker?: string;
  __corpName?: string;
  __change?: number;
  __close?: number;
  __open?: number;
  __high?: number;
  __low?: number;
  __volume?: number;
  __tradingValue?: number;
  __tradingDate?: string;
};

type TopMatch = {
  ticker: string;
  corpName: string;
  newsCount: number;
  change: number;
  close: number;
  tradingValue: number;
  tradingDate?: string;
  impactAvg: number;
};

const UI = {
  bg: '#050914',
  panel: 'rgba(10,16,28,0.74)',
  panel2: 'rgba(8,13,24,0.88)',
  glass: 'rgba(255,255,255,0.045)',
  glass2: 'rgba(255,255,255,0.025)',
  stroke: 'rgba(148,163,184,0.16)',
  stroke2: 'rgba(148,163,184,0.26)',
  text: '#f8fafc',
  dim: 'rgba(226,232,240,0.78)',
  sub: 'rgba(148,163,184,0.88)',
  moreDim: 'rgba(148,163,184,0.68)',
  blue: '#60a5fa',
  cyan: '#67e8f9',
  green: '#34d399',
  amber: '#fbbf24',
  red: '#fb7185',
  violet: '#a78bfa',
  slate: '#94a3b8',
  shadow: '0 22px 64px rgba(0,0,0,0.36)',
};

type DashboardSector =
  | 'all'
  | 'bio_healthcare'
  | 'chip'
  | 'ev'
  | 'energy'
  | 'crypto'
  | 'shipbuilding'
  | 'shipping'
  | 'petrochem'
  | 'steel'
  | 'defense';

type DashboardSectorStat = {
  issues: number;
  stocks: number;
  heat: number;
  up: number;
  down: number;
};

const DASHBOARD_SECTORS: DashboardSector[] = [
  'all',
  'bio_healthcare',
  'chip',
  'ev',
  'energy',
  'crypto',
  'defense',
  'shipbuilding',
  'shipping',
  'petrochem',
  'steel',
];

const DASHBOARD_SECTOR_META: Record<DashboardSector, { label: string; short: string; accent: string; desc: string }> = {
  all: { label: '전체', short: '전체', accent: UI.cyan, desc: '전체 이슈' },
  bio_healthcare: { label: '바이오/헬스케어', short: '바이오', accent: UI.green, desc: '의료·제약' },
  chip: { label: '반도체/칩', short: '반도체', accent: UI.blue, desc: 'AI·장비·소재' },
  ev: { label: '전기차/배터리', short: '배터리', accent: UI.amber, desc: 'EV·2차전지' },
  energy: { label: '에너지/친환경', short: '에너지', accent: UI.green, desc: '전력·기후' },
  crypto: { label: '핀테크/암호화폐', short: '핀테크', accent: UI.violet, desc: '금융·가상자산' },
  defense: { label: '방위/항공', short: '방산', accent: UI.red, desc: '국방·우주' },
  shipbuilding: { label: '조선/해양', short: '조선', accent: UI.cyan, desc: '선박·해양' },
  shipping: { label: '해운/물류', short: '해운', accent: UI.blue, desc: '운임·항만' },
  petrochem: { label: '정유/석유화학', short: '석유화학', accent: UI.amber, desc: '유가·화학' },
  steel: { label: '철강/비철', short: '철강', accent: UI.slate, desc: '소재·금속' },
};

function normalizeDashboardSector(value?: any): DashboardSector {
  const normalized = normalizeSector(value);
  return DASHBOARD_SECTORS.includes(normalized as DashboardSector) ? (normalized as DashboardSector) : 'all';
}

function buildSectorStats(rawFeeds: any[]): Record<DashboardSector, DashboardSectorStat> {
  const stats = {} as Record<DashboardSector, DashboardSectorStat>;

  DASHBOARD_SECTORS.forEach((sector) => {
    const feeds = sector === 'all' ? rawFeeds : rawFeeds.filter((feed) => feedMatchesSector(feed, sector));
    const rows = buildStockRowsFromFeeds(rawFeeds, sector);
    const stocks = rows.filter((row: any) => row?.ticker && row.ticker !== '__POLICY__');
    const heatSum = feeds.reduce((sum, feed) => sum + getImpact(feed), 0);
    const heat = feeds.length ? Math.round(heatSum / feeds.length) : 0;
    const up = stocks.filter((row: any) => getChangePct(row) > 0).length;
    const down = stocks.filter((row: any) => getChangePct(row) < 0).length;

    stats[sector] = {
      issues: feeds.length,
      stocks: stocks.length,
      heat,
      up,
      down,
    };
  });

  return stats;
}

function DashboardSectorTabs({
  value,
  stats,
  onChange,
  isMobile,
}: {
  value: DashboardSector;
  stats: Record<DashboardSector, DashboardSectorStat>;
  onChange: (sector: DashboardSector) => void;
  isMobile: boolean;
}) {
  const selected = DASHBOARD_SECTOR_META[value] ?? DASHBOARD_SECTOR_META.all;
  const selectedStats = stats[value] ?? { issues: 0, stocks: 0, heat: 0, up: 0, down: 0 };

  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: isMobile ? 2.2 : 2.8,
        border: `1px solid ${UI.stroke}`,
        background:
          'radial-gradient(circle at 0% 0%, rgba(103,232,249,0.055), transparent 34%), linear-gradient(180deg, rgba(8,13,24,0.82), rgba(5,9,20,0.74))',
        p: isMobile ? 0.9 : 1.05,
        overflow: 'hidden',
        boxShadow: '0 16px 38px rgba(0,0,0,0.20)',
      }}
    >
      <Stack
        direction={isMobile ? 'column' : 'row'}
        justifyContent="space-between"
        alignItems={isMobile ? 'flex-start' : 'center'}
        spacing={0.85}
        sx={{ mb: 0.9 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={0.6} alignItems="center" useFlexGap flexWrap="wrap">
            <Chip
              size="small"
              label="Sector Map"
              sx={{
                height: 24,
                bgcolor: 'rgba(103,232,249,0.12)',
                border: '1px solid rgba(103,232,249,0.24)',
                color: '#cffafe',
                fontWeight: 950,
              }}
            />
            <Typography sx={{ color: UI.text, fontWeight: 950, fontSize: isMobile ? 12.5 : 13.5 }}>
              {selected.label}
            </Typography>
          </Stack>
          <Typography sx={{ color: UI.sub, fontSize: isMobile ? 10.7 : 11.5, mt: 0.32, lineHeight: 1.5 }}>
            {selectedStats.issues.toLocaleString()}건 이슈 · 연결 종목 {selectedStats.stocks.toLocaleString()}개 · 상승 {selectedStats.up} / 하락 {selectedStats.down}
          </Typography>
        </Box>

        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
          <Chip size="small" label={`HEAT ${selectedStats.heat}`} sx={sectorMetaChipSx(selected.accent)} />
          <Chip size="small" label={`▲ ${selectedStats.up}`} sx={sectorMetaChipSx(UI.green)} />
          <Chip size="small" label={`▼ ${selectedStats.down}`} sx={sectorMetaChipSx(UI.red)} />
        </Stack>
      </Stack>

      <Box
        className="pi-scrollbar"
        sx={{
          display: 'flex',
          gap: isMobile ? 0.65 : 0.75,
          overflowX: 'auto',
          pb: 0.15,
          mx: -0.2,
          px: 0.2,
          '&::-webkit-scrollbar': { height: 6 },
        }}
      >
        {DASHBOARD_SECTORS.map((sector) => {
          const meta = DASHBOARD_SECTOR_META[sector];
          const stat = stats[sector] ?? { issues: 0, stocks: 0, heat: 0, up: 0, down: 0 };
          const active = value === sector;

          return (
            <Box
              key={sector}
              component="button"
              type="button"
              onClick={() => onChange(sector)}
              aria-pressed={active}
              title={`${meta.label} · ${stat.issues}건`}
              style={{ appearance: 'none' }}
              sx={{
                flex: '0 0 auto',
                width: isMobile ? 132 : 150,
                minHeight: isMobile ? 54 : 60,
                borderRadius: 2,
                border: active ? `1px solid ${meta.accent}66` : '1px solid rgba(148,163,184,0.14)',
                background: active
                  ? `linear-gradient(180deg, ${hexToRgba(meta.accent, 0.20)}, rgba(255,255,255,0.045))`
                  : 'rgba(255,255,255,0.038)',
                color: UI.text,
                textAlign: 'left',
                cursor: 'pointer',
                padding: isMobile ? '9px 10px' : '10px 12px',
                boxShadow: active ? `0 0 0 1px ${hexToRgba(meta.accent, 0.12)} inset, 0 10px 22px ${hexToRgba(meta.accent, 0.08)}` : 'none',
                transition: '160ms ease',
                '&:hover': {
                  borderColor: `${meta.accent}55`,
                  background: `linear-gradient(180deg, ${hexToRgba(meta.accent, 0.14)}, rgba(255,255,255,0.045))`,
                  transform: 'translateY(-1px)',
                },
              }}
            >
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: 999,
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: hexToRgba(meta.accent, active ? 0.22 : 0.12),
                    border: `1px solid ${hexToRgba(meta.accent, 0.34)}`,
                    color: meta.accent,
                    flex: '0 0 auto',
                    fontWeight: 1000,
                    fontSize: 12,
                  }}
                >
                  {sector === 'all' ? '✦' : meta.short.slice(0, 1)}
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ color: active ? UI.text : '#dbeafe', fontWeight: 950, fontSize: isMobile ? 11.5 : 12.2, lineHeight: 1.15 }} noWrap>
                    {isMobile ? meta.short : meta.label}
                  </Typography>
                  <Typography sx={{ color: UI.sub, fontWeight: 800, fontSize: 9.7, mt: 0.25 }} noWrap>
                    {stat.issues ? `${stat.issues.toLocaleString()}건 · HEAT ${stat.heat}` : meta.desc}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function sectorMetaChipSx(color: string) {
  return {
    height: 24,
    bgcolor: hexToRgba(color, 0.12),
    border: `1px solid ${hexToRgba(color, 0.28)}`,
    color,
    fontWeight: 950,
    '& .MuiChip-label': { px: 0.8, fontSize: 10.4 },
  };
}

function hexToRgba(hex: string, alpha: number) {
  const clean = hex.replace('#', '');
  if (clean.length !== 6) return `rgba(148,163,184,${alpha})`;
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function fmtDate(s?: string) {
  if (!s) return '';
  const d = dayjs(s);
  return d.isValid() ? d.format('YYYY.MM.DD HH:mm') : '';
}

function fmtDay(s?: string) {
  if (!s) return '—';
  const d = dayjs(s);
  return d.isValid() ? d.format('YYYY.MM.DD') : s;
}

function fromNowKo(s?: string) {
  if (!s) return '';
  const d = dayjs(s);
  if (!d.isValid()) return '';

  const diffMin = Math.max(0, dayjs().diff(d, 'minute'));
  if (diffMin < 1) return '방금 전';
  if (diffMin < 60) return `${diffMin}분 전`;

  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `${diffHour}시간 전`;

  return `${Math.floor(diffHour / 24)}일 전`;
}

function toText(v: any): string {
  if (v === null || v === undefined) return '';
  return String(v).trim();
}

function toLower(v: any): string {
  return toText(v).toLowerCase();
}

function asNumber(v: any, fallback = 0): number {
  if (v === null || v === undefined || v === '') return fallback;

  if (typeof v === 'string') {
    const clean = v.replace(/,/g, '').trim();
    if (!clean || clean === 'null' || clean === 'undefined') return fallback;

    const n = Number(clean);
    return Number.isFinite(n) ? n : fallback;
  }

  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function clamp(v: number, min = 0, max = 100) {
  return Math.max(min, Math.min(max, v));
}

function parseTags(raw: any): string[] {
  const out: string[] = [];

  if (!raw) return out;

  if (Array.isArray(raw)) {
    raw.forEach((v) => {
      const s = String(v ?? '').trim();
      if (s) out.push(s);
    });
  } else if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        parsed.forEach((v) => {
          const s = String(v ?? '').trim();
          if (s) out.push(s);
        });
      } else {
        raw.split(',').forEach((v) => {
          const s = String(v ?? '').trim();
          if (s) out.push(s);
        });
      }
    } catch {
      raw.split(',').forEach((v) => {
        const s = String(v ?? '').trim();
        if (s) out.push(s);
      });
    }
  }

  return Array.from(new Set(out));
}

function normalizeArray(value: any): any[] {
  if (!value) return [];
  if (Array.isArray(value)) return value;

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed;
      if (parsed && typeof parsed === 'object') return [parsed];
      return [];
    } catch {
      return [];
    }
  }

  if (typeof value === 'object') return [value];

  return [];
}

function normalizeSector(value?: any): string {
  const x = toLower(value);

  if (!x || x === 'all' || x === '전체') return 'all';

  if (
    [
      'bio',
      'bio/healthcare',
      'bio-healthcare',
      'bio_healthcare',
      'healthcare',
      '바이오',
      '헬스',
      '헬스케어',
      '제약',
      '의료',
    ].includes(x)
  ) {
    return 'bio_healthcare';
  }

  if (
    [
      'chip',
      'chips',
      'semiconductor',
      'semiconductors',
      'semiconductor/it',
      'it/semiconductor',
      '반도체',
      'it',
      'ict',
    ].includes(x)
  ) {
    return 'chip';
  }

  if (
    [
      'ev',
      'ev/battery',
      'battery',
      'batteries',
      '전기차',
      '배터리',
      '이차전지',
      '2차전지',
    ].includes(x)
  ) {
    return 'ev';
  }

  if (
    [
      'energy',
      'cleanenergy',
      'clean-tech',
      'cleantech',
      'renewable',
      'renewables',
      '에너지',
      '신재생',
      '친환경',
      '그린',
      '그린에너지',
    ].includes(x)
  ) {
    return 'energy';
  }

  if (
    [
      'crypto',
      'fintech',
      'fintech/crypto',
      '가상자산',
      '디지털자산',
      '핀테크',
      '블록체인',
      '암호화폐',
    ].includes(x)
  ) {
    return 'crypto';
  }

  if (
    [
      'defense',
      'defence',
      'aerospace',
      '방산',
      '국방',
      '우주항공',
      '항공우주',
    ].includes(x)
  ) {
    return 'defense';
  }

  if (
    [
      'shipbuilding',
      'shipbuilding/maritime',
      'maritime',
      'marine',
      '조선',
      '해양',
      '해사',
      '선박',
      '조선해양',
    ].includes(x)
  ) {
    return 'shipbuilding';
  }

  if (
    [
      'shipping',
      'shipping/logistics',
      'logistics',
      'ports',
      'port',
      '해운',
      '물류',
      '항만',
    ].includes(x)
  ) {
    return 'shipping';
  }

  if (
    [
      'petrochem',
      'petchem',
      'oil',
      'o&g',
      'oil&gas',
      'refining',
      '정유',
      '석유화학',
      '석화',
      '에너지화학',
    ].includes(x)
  ) {
    return 'petrochem';
  }

  if (
    [
      'steel',
      'nonferrous',
      'non-ferrous',
      'metals',
      '철강',
      '비철',
      '금속',
      '철강/소재',
    ].includes(x)
  ) {
    return 'steel';
  }

  return x;
}

function feedMatchesSector(feed: any, sector: any): boolean {
  const target = normalizeSector(sector);
  if (!target || target === 'all') return true;

  return normalizeSector(feed?.sector) === target;
}

function getFeedId(feed: any): string {
  return toText(feed?.feed_id ?? feed?.feedId ?? feed?.id);
}

function getImpact(feed: any): number {
  const v =
    feed?.impact_score ??
    feed?.impactScore ??
    feed?.analysis_impact_score ??
    feed?.analysisImpactScore ??
    feed?.impact ??
    0;

  const n = Number(v);
  if (Number.isFinite(n)) return clamp(n);

  return 0;
}

function pickTitle(f: any) {
  return String(f?.title_ko ?? f?.titleKo ?? f?.title ?? '').trim();
}

function pickSummary(f: any) {
  return String(f?.summary_ko ?? f?.summaryKo ?? f?.summary ?? '').trim();
}

function feedHasTag(feed: any, tag: string) {
  const tags = parseTags(feed?.issue_tags ?? feed?.issueTags);
  return tags.includes(tag);
}

function getFeedRelatedStocks(feed: any): any[] {
  const raw =
    feed?.related_stock_reactions ??
    feed?.relatedStockReactions ??
    feed?.related_stocks_reactions ??
    feed?.stock_reactions ??
    feed?.stockReactions ??
    [];

  const reactions = normalizeArray(raw).filter(Boolean);
  if (reactions.length > 0) return reactions;

  const mapped =
    feed?.mapped_tickers ??
    feed?.mappedTickers ??
    feed?.tickers ??
    feed?.ticker ??
    [];

  return normalizeArray(mapped)
    .map((x) => {
      if (typeof x === 'string') {
        return {
          ticker: x,
          relation_type: 'mapped',
        };
      }

      return x;
    })
    .filter(Boolean);
}

function getStockTicker(stock: any): string {
  return toText(
    stock?.ticker ??
      stock?.code ??
      stock?.stock_code ??
      stock?.stockCode ??
      stock?.direct_ticker ??
      stock?.directTicker,
  );
}

function getStockCorpName(stock: any, fallbackTicker = ''): string {
  return toText(
    stock?.corpName ??
      stock?.corp_name ??
      stock?.name ??
      stock?.stockName ??
      stock?.stock_name ??
      stock?.ticker_name ??
      stock?.tickerName ??
      fallbackTicker,
  );
}

function getChangePct(s: any): number {
  const v =
    s?.priceChangePercentage ??
    s?.price_change_percentage ??
    s?.change_rate ??
    s?.changeRate ??
    s?.changePct ??
    s?.change_percentage ??
    s?.pct_change ??
    s?.pctChange ??
    s?.chg_pct ??
    s?.diff_pct ??
    s?.change ??
    0;

  return asNumber(v, 0);
}

function getClosePrice(s: any): number {
  return asNumber(
    s?.close ??
      s?.price ??
      s?.currentPrice ??
      s?.current_price ??
      s?.last_price ??
      s?.end_price,
    0,
  );
}

function getOpenPrice(s: any): number {
  return asNumber(s?.open ?? s?.start_price ?? s?.openPrice ?? s?.open_price, 0);
}

function getHighPrice(s: any): number {
  return asNumber(s?.high ?? s?.highPrice ?? s?.high_price, 0);
}

function getLowPrice(s: any): number {
  return asNumber(s?.low ?? s?.lowPrice ?? s?.low_price, 0);
}

function getVolume(s: any): number {
  return asNumber(s?.volume ?? s?.tradingVolume ?? s?.trade_volume ?? s?.cum_volume, 0);
}

function getTradingValue(s: any): number {
  return asNumber(
    s?.tradingValue ??
      s?.trading_value ??
      s?.amount ??
      s?.marketTradingValue ??
      s?.tradingAmount ??
      s?.trade_amount,
    0,
  );
}

function getRowDate(s: any): string {
  const raw =
    s?.date ??
    s?.tradingDate ??
    s?.trading_date ??
    s?.latestDate ??
    s?.asOfDate ??
    s?.asofDate ??
    s?.base_date ??
    '';

  const str = String(raw ?? '').trim();
  if (!str || str === 'null' || str === 'undefined') return '';

  return str;
}

function fmtPrice(v: number) {
  if (!Number.isFinite(v) || v <= 0) return '—';
  return `${Math.round(v).toLocaleString()}원`;
}

function fmtVolume(v: number) {
  if (!Number.isFinite(v) || v <= 0) return '—';
  return v.toLocaleString();
}

function fmtTradingValue(v: number) {
  if (!Number.isFinite(v) || v <= 0) return '—';

  const eok = v / 100000000;

  if (eok >= 10000) {
    const jo = eok / 10000;
    return jo >= 10 ? `${jo.toFixed(1)}조` : `${jo.toFixed(2)}조`;
  }

  if (eok >= 1000) return `${Math.round(eok).toLocaleString()}억`;
  if (eok >= 100) return `${Math.round(eok)}억`;
  if (eok >= 10) return `${eok.toFixed(1)}억`;

  return `${eok.toFixed(2)}억`;
}

function fmtPct(v: number, digits = 2) {
  if (!Number.isFinite(v)) return '0.00%';
  return `${v >= 0 ? '+' : ''}${v.toFixed(digits)}%`;
}

function getDirection(change: number): ReactionDirection {
  if (change > 0) return 'up';
  if (change < 0) return 'down';
  return 'flat';
}

function directionLabel(change: number) {
  if (change > 0) return '상승 반응 관측';
  if (change < 0) return '하락 반응 관측';
  return '중립 반응 관측';
}

function judgmentTone(label: JudgmentLabel): 'good' | 'bad' | 'neutral' {
  if (label === '우선 추적' || label === '관심 유지') return 'good';
  if (label === '진입 경계' || label === '제외') return 'bad';
  return 'neutral';
}

function toneColor(tone: 'good' | 'bad' | 'neutral') {
  if (tone === 'good') return { bg: 'rgba(52,211,153,.14)', bd: 'rgba(52,211,153,.26)', fg: '#b9ffd2' };
  if (tone === 'bad') return { bg: 'rgba(251,113,133,.13)', bd: 'rgba(251,113,133,.26)', fg: '#ffd2dc' };
  return { bg: 'rgba(148,163,184,.12)', bd: 'rgba(148,163,184,.22)', fg: '#dbeafe' };
}

function buildJudgment(feed: SignalFeed): JudgmentLabel {
  const impact = getImpact(feed);
  const change = asNumber(feed?.__change ?? 0, 0);
  const absChange = Math.abs(change);
  const title = pickTitle(feed);
  const summary = pickSummary(feed);
  const tags = parseTags(feed?.issue_tags ?? feed?.issueTags);
  const text = `${title} ${summary} ${tags.join(' ')}`.toLowerCase();

  if (/(정정|소송|거절|불성실|제재|유상증자|cb|bw|악재|리스크|금지|조사)/i.test(text)) return '진입 경계';
  if (impact >= 80 && absChange >= 7) return '눌림 대기';
  if (impact >= 74 && change > 0) return '우선 추적';
  if (impact >= 62) return '관심 유지';
  if (change < 0 && impact >= 50) return '수급 확인';
  if (impact < 35) return '제외';

  return '수급 확인';
}

function extractImpactedTargets(feed: SignalFeed): string[] {
  const ticker = String(feed?.__ticker ?? '').trim();
  const corp = String(feed?.__corpName ?? '').trim();
  const tags = parseTags(feed?.issue_tags ?? feed?.issueTags);
  const out = [corp, ticker, ...tags.map((v) => `#${v}`)].filter(Boolean);

  return Array.from(new Set(out)).slice(0, 6);
}

function getActionTone(change: number, impact: number): string {
  if (change >= 4 && impact >= 65) {
    return '가격 반응이 이미 강하게 관측됩니다. 신규 접근보다 거래대금 지속성과 눌림 구간을 먼저 확인하세요.';
  }

  if (change > 0 && impact >= 55) {
    return '상승 반응은 확인되지만, 단일 기사보다 반복 이슈와 수급 동반 여부를 같이 확인하세요.';
  }

  if (change < -4 && impact >= 65) {
    return '강한 이슈에도 가격이 약합니다. 정책 수혜보다 실적·수급·환율 변수를 먼저 분리해야 합니다.';
  }

  if (change < 0 && impact >= 55) {
    return '이슈 강도와 가격 방향이 엇갈립니다. 외국인/기관 수급 회복 전까지 관찰 우선입니다.';
  }

  if (impact >= 65) {
    return '이슈 강도는 높지만 최신 가격 반응은 제한적입니다. 후속 기사와 거래대금 증가를 확인하세요.';
  }

  return '즉시 판단보다 관련 뉴스, 차트, 거래 흐름을 함께 확인하는 구간입니다.';
}

function getImpactInterpretation(impact: number, change: number): string {
  if (impact >= 75 && change > 0) return '정책 이슈 강도와 상승 반응이 함께 관측됩니다.';
  if (impact >= 75 && change < 0) return '정책 이슈 강도는 높지만 최신 가격 반응은 반대 방향입니다.';
  if (impact >= 60 && change > 0) return '중상 수준 이슈가 최근 OHLCV에 상승 방향으로 연결됩니다.';
  if (impact >= 60 && change < 0) return '중상 수준 이슈지만 최근 OHLCV에서는 하락 방향이 함께 관측됩니다.';
  if (Math.abs(change) >= 3) return '가격 변동은 크지만 정책 연결 강도는 중간 이하입니다.';

  return '정책 참고도는 있으나 최신 가격 반응은 제한적입니다.';
}

function buildFeedGrammar(feed: SignalFeed): SummaryGrammar {
  const impact = getImpact(feed);
  const corp = String(feed?.__corpName ?? feed?.__ticker ?? '해당 종목').trim();
  const tradingDate = String(feed?.__tradingDate ?? '').trim();
  const judgment = buildJudgment(feed);
  const change = asNumber(feed?.__change ?? 0, 0);

  const whatHappened = pickTitle(feed) || '정책/이슈 카드';

  const whyImportant =
    impact >= 75
      ? '이슈 강도가 높아 최신 OHLCV와 함께 우선 해석해야 하는 카드입니다.'
      : impact >= 60
        ? '중상 수준 영향도로 종목 반응과 함께 볼 가치가 있습니다.'
        : '정책 참고도는 있으나 가격 반응과 함께 선별적으로 봐야 합니다.';

  const nextAction =
    judgment === '우선 추적'
      ? '관련 종목의 거래대금과 후속 기사 확산 여부를 계속 추적하세요.'
      : judgment === '관심 유지'
        ? '직접 연결 종목의 최근 OHLCV와 수급 변화를 먼저 확인하세요.'
        : judgment === '눌림 대기'
          ? '즉시 추격보다 가격 조정과 거래 흐름 재확인을 기다리세요.'
          : judgment === '진입 경계'
            ? '정책 이슈보다 리스크 요인을 먼저 점검하세요.'
            : judgment === '제외'
              ? '현재 우선순위에서는 제외하고 후속 변화만 체크하세요.'
              : '가격 방향과 외국인·기관 수급이 다시 같은 방향으로 맞는지 확인하세요.';

  return {
    whatHappened,
    whyImportant: `${whyImportant} ${corp} 기준 최신 OHLCV ${fmtDay(tradingDate)}와 연결됩니다.`,
    impactedTargets: extractImpactedTargets(feed),
    currentJudgment: judgment,
    nextAction,
    riskLine:
      change >= 5
        ? '단기 반응이 이미 강할 수 있습니다. 선반영·급등 피로를 반드시 확인해야 합니다.'
        : change < 0 && impact >= 60
          ? '이슈 강도와 가격 방향이 엇갈립니다. 수급 이탈 또는 시장 리스크를 점검하세요.'
          : '후속 기사 없이 가격만 움직이면 단기 테마성 반응일 수 있습니다.',
  };
}

function uniqByFeedId(list: SignalFeed[]): SignalFeed[] {
  const seen = new Set<string>();
  const out: SignalFeed[] = [];

  for (const x of list) {
    const id = String(x?.feed_id ?? x?.feedId ?? x?.id ?? '');
    const fallback = `${String(x?.title_ko ?? x?.titleKo ?? x?.title ?? '')}_${String(
      x?.published_at ?? x?.publishedAt ?? x?.date ?? '',
    )}_${String(x?.__ticker ?? '')}`;

    const key = id || fallback;

    if (!key) continue;
    if (seen.has(key)) continue;

    seen.add(key);
    out.push(x);
  }

  return out;
}

function getRawFeedsFromState(state: any): any[] {
  const candidates = [
    state?.feeds,
    state?.feedList,
    state?.todayFeeds,
    state?.rawFeeds,
    state?.items,
    state?.data,
    state?.rows,
    state?.feedRows,
    state?.today,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate) && candidate.length > 0) return candidate;
  }

  return [];
}

function isFeedRow(row: any): boolean {
  return Boolean(
    row &&
      (row.feed_id ||
        row.feedId ||
        row.title ||
        row.title_ko ||
        row.titleKo ||
        row.related_stock_reactions ||
        row.relatedStockReactions),
  );
}

function hasNewsListRow(row: any): boolean {
  return Boolean(row && Array.isArray(row.newsList));
}

function buildStockRowsFromFeeds(rawFeeds: any[], sector?: any): StockChange[] {
  const grouped: Record<string, any> = {};

  for (const feed of rawFeeds) {
    if (!feedMatchesSector(feed, sector)) continue;

    const relatedStocks = getFeedRelatedStocks(feed);

    if (!relatedStocks.length) {
      const pseudoKey = `__POLICY__:${String(feed?.feed_id ?? feed?.feedId ?? feed?.id ?? Math.random())}`;

      if (!grouped[pseudoKey]) {
        grouped[pseudoKey] = {
          ticker: '__POLICY__',
          corpName: '정책 이슈',
          priceChangePercentage: 0,
          close: 0,
          volume: 0,
          tradingValue: 0,
          date: feed?.published_at ?? feed?.publishedAt ?? feed?.created_at ?? feed?.createdAt ?? '',
          newsList: [],
        };
      }

      grouped[pseudoKey].newsList.push(feed);
      continue;
    }

    for (const stock of relatedStocks) {
      const ticker = getStockTicker(stock);
      if (!ticker) continue;

      const corpName = getStockCorpName(stock, ticker);
      const change = getChangePct(stock);
      const close = getClosePrice(stock);
      const volume = getVolume(stock);
      const tradingValue = getTradingValue(stock);
      const date = getRowDate(stock);

      if (!grouped[ticker]) {
        grouped[ticker] = {
          ticker,
          corpName,
          name: stock?.name ?? corpName,
          market: stock?.market,

          priceChangePercentage: change,
          price_change_percentage: change,
          change_rate: change,
          pct_change: change,

          close,
          volume,

          tradingValue,
          trading_value: tradingValue,

          date,
          newsList: [],
        };
      } else {
        const current = grouped[ticker];

        if (!current.corpName && corpName) current.corpName = corpName;
        if (!current.name && (stock?.name || corpName)) current.name = stock?.name ?? corpName;
        if (!current.market && stock?.market) current.market = stock.market;
        if (!current.close && close) current.close = close;
        if (!current.volume && volume) current.volume = volume;
        if (!current.tradingValue && tradingValue) current.tradingValue = tradingValue;
        if (!current.trading_value && tradingValue) current.trading_value = tradingValue;
        if (!current.date && date) current.date = date;

        if (asNumber(current.priceChangePercentage, 0) === 0 && change !== 0) {
          current.priceChangePercentage = change;
          current.price_change_percentage = change;
          current.change_rate = change;
          current.pct_change = change;
        }
      }

      const feedId = getFeedId(feed);
      const exists = grouped[ticker].newsList.some((x: any) => getFeedId(x) === feedId);

      if (!exists) grouped[ticker].newsList.push(feed);
    }
  }

  return Object.values(grouped).sort((a, b) => {
    const newsDiff = Number(b.newsList?.length ?? 0) - Number(a.newsList?.length ?? 0);
    if (newsDiff !== 0) return newsDiff;

    const changeDiff = Math.abs(getChangePct(b)) - Math.abs(getChangePct(a));
    if (changeDiff !== 0) return changeDiff;

    return getTradingValue(b) - getTradingValue(a);
  });
}

function normalizeDashboardDisplayRows(displayList: any[], rawFeeds: any[], sector?: any): StockChange[] {
  const rows = Array.isArray(displayList) ? displayList : [];

  if (rows.length > 0) {
    const first = rows[0];

    if (hasNewsListRow(first)) return rows as StockChange[];
    if (isFeedRow(first)) return buildStockRowsFromFeeds(rows, sector);
  }

  return buildStockRowsFromFeeds(rawFeeds, sector);
}

function heatPalette(heat: number) {
  if (heat >= 75) {
    return {
      ring: UI.red,
      glow: 'rgba(251,113,133,0.22)',
      chip: 'rgba(251,113,133,0.14)',
      line: 'linear-gradient(90deg, rgba(251,113,133,0.95), rgba(251,191,36,0.88))',
    };
  }

  if (heat >= 55) {
    return {
      ring: UI.amber,
      glow: 'rgba(251,191,36,0.18)',
      chip: 'rgba(251,191,36,0.13)',
      line: 'linear-gradient(90deg, rgba(251,191,36,0.95), rgba(103,232,249,0.82))',
    };
  }

  return {
    ring: UI.green,
    glow: 'rgba(52,211,153,0.17)',
    chip: 'rgba(52,211,153,0.12)',
    line: 'linear-gradient(90deg, rgba(52,211,153,0.88), rgba(103,232,249,0.82))',
  };
}

function RingIcon({ heat, size = 44 }: { heat: number; size?: number }) {
  const c = heatPalette(heat);

  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        border: `3px solid ${c.ring}`,
        boxShadow: `0 0 18px ${c.glow}`,
        display: 'grid',
        placeItems: 'center',
        flex: '0 0 auto',
      }}
    >
      <Box
        sx={{
          width: Math.max(16, size * 0.46),
          height: Math.max(16, size * 0.46),
          borderRadius: '50%',
          background: c.chip,
          border: '1px solid rgba(255,255,255,0.10)',
        }}
      />
    </Box>
  );
}

function JudgmentBadge({ label }: { label: JudgmentLabel }) {
  const palette = toneColor(judgmentTone(label));

  return (
    <Chip
      size="small"
      label={label}
      sx={{
        bgcolor: palette.bg,
        border: `1px solid ${palette.bd}`,
        color: palette.fg,
        fontWeight: 950,
        height: 24,
      }}
    />
  );
}

function HeatBar({ heat }: { heat: number }) {
  const c = heatPalette(heat);

  return (
    <Box sx={{ width: '100%', height: 6, borderRadius: 99, background: 'rgba(148,163,184,0.12)', overflow: 'hidden' }}>
      <Box
        sx={{
          width: `${Math.max(6, Math.min(100, heat))}%`,
          height: '100%',
          background: c.line,
          borderRadius: 99,
        }}
      />
    </Box>
  );
}

function SectionHeader({
  title,
  sub,
  right,
  isMobile,
}: {
  title: string;
  sub?: string;
  right?: React.ReactNode;
  isMobile?: boolean;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 1,
        alignItems: isMobile ? 'flex-start' : 'flex-end',
        flexDirection: isMobile ? 'column' : 'row',
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ color: UI.text, fontWeight: 950, fontSize: isMobile ? 13 : 14.5 }}>
          {title}
        </Typography>
        {sub ? (
          <Typography sx={{ color: UI.sub, fontSize: isMobile ? 10.8 : 12, mt: 0.35, lineHeight: 1.55 }}>
            {sub}
          </Typography>
        ) : null}
      </Box>

      {right ? <Box sx={{ flex: '0 0 auto', width: isMobile ? '100%' : 'auto' }}>{right}</Box> : null}
    </Box>
  );
}

function StatusPill({ loading }: { loading: boolean }) {
  return (
    <Chip
      size="small"
      icon={
        <Box
          component="span"
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: loading ? UI.blue : UI.green,
            display: 'inline-block',
            ml: 0.4,
          }}
        />
      }
      label={loading ? '데이터 갱신 중' : '최신 OHLCV 반영'}
      sx={{
        bgcolor: loading ? 'rgba(96,165,250,0.14)' : 'rgba(52,211,153,0.12)',
        border: '1px solid rgba(148,163,184,0.18)',
        color: UI.dim,
        fontWeight: 900,
        height: 28,
        maxWidth: '100%',
      }}
    />
  );
}

function MiniKpi({
  icon,
  title,
  value,
  sub,
  isMobile,
}: {
  icon: React.ReactNode;
  title: string;
  value: string;
  sub?: string;
  isMobile?: boolean;
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: isMobile ? 0.95 : 1.25,
        borderRadius: 2.4,
        border: `1px solid ${UI.stroke}`,
        background: 'rgba(8,12,22,0.55)',
        minWidth: 0,
        minHeight: isMobile ? 82 : 96,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 0.8 : 1 }}>
        <Box
          sx={{
            width: isMobile ? 30 : 36,
            height: isMobile ? 30 : 36,
            borderRadius: 2,
            display: 'grid',
            placeItems: 'center',
            border: '1px solid rgba(148,163,184,0.18)',
            background: 'rgba(2,6,23,0.35)',
            color: '#cfe0ff',
            flex: '0 0 auto',
          }}
        >
          {icon}
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color: UI.dim, fontSize: isMobile ? 10 : 11, fontWeight: 900 }}>
            {title}
          </Typography>
          <Typography sx={{ color: UI.text, fontSize: isMobile ? 14.5 : 17, fontWeight: 950, lineHeight: 1.15 }}>
            {value}
          </Typography>
          {sub ? (
            <Typography sx={{ color: UI.sub, fontSize: isMobile ? 10 : 11, fontWeight: 800, lineHeight: 1.35 }}>
              {sub}
            </Typography>
          ) : null}
        </Box>
      </Box>
    </Paper>
  );
}

function OhlcvMicroLine({
  tradingDate,
  close,
  changePct,
  volume,
  tradingValue,
}: {
  tradingDate?: string;
  close: number;
  changePct: number;
  volume: number;
  tradingValue: number;
}) {
  const direction = getDirection(changePct);

  return (
    <Box sx={{ display: 'flex', gap: 0.55, alignItems: 'center', flexWrap: 'wrap', mt: 0.7 }}>
      <Chip
        size="small"
        label={`OHLCV ${fmtDay(tradingDate)}`}
        sx={microChipSx('blue')}
      />
      <Chip
        size="small"
        label={`종가 ${fmtPrice(close)}`}
        sx={microChipSx('slate')}
      />
      <Chip
        size="small"
        label={`등락 ${fmtPct(changePct)}`}
        sx={microChipSx(direction === 'up' ? 'green' : direction === 'down' ? 'red' : 'slate')}
      />
      <Chip
        size="small"
        label={`거래대금 ${fmtTradingValue(tradingValue)}`}
        sx={microChipSx('amber')}
      />
      <Chip
        size="small"
        label={`거래량 ${fmtVolume(volume)}`}
        sx={microChipSx('slate')}
      />
    </Box>
  );
}

function microChipSx(tone: 'green' | 'red' | 'amber' | 'blue' | 'slate') {
  const map = {
    green: { bg: 'rgba(52,211,153,.14)', bd: 'rgba(52,211,153,.22)', fg: '#b9ffd2' },
    red: { bg: 'rgba(251,113,133,.14)', bd: 'rgba(251,113,133,.22)', fg: '#ffd2dc' },
    amber: { bg: 'rgba(251,191,36,.12)', bd: 'rgba(251,191,36,.20)', fg: '#ffe9b3' },
    blue: { bg: 'rgba(96,165,250,.12)', bd: 'rgba(96,165,250,.20)', fg: '#cfe0ff' },
    slate: { bg: 'rgba(148,163,184,.10)', bd: 'rgba(148,163,184,.16)', fg: '#dcecff' },
  }[tone];

  return {
    bgcolor: map.bg,
    border: `1px solid ${map.bd}`,
    color: map.fg,
    fontWeight: 900,
    height: 23,
    '& .MuiChip-label': { px: 0.85, fontSize: 10.4 },
  };
}

function SummaryGrammarMini({ grammar, isMobile }: { grammar: SummaryGrammar; isMobile: boolean }) {
  const rows = [
    ['무엇이 일어났나', grammar.whatHappened],
    ['왜 중요한가', grammar.whyImportant],
    ['누구에게 영향인가', grammar.impactedTargets.join(', ') || '대상 정리 필요'],
    ['지금 판단', grammar.currentJudgment],
    ['다음 행동', grammar.nextAction],
  ];

  return (
    <Box
      sx={{
        mt: 0.75,
        p: isMobile ? 0.9 : 1,
        borderRadius: 2,
        border: '1px solid rgba(148,163,184,0.12)',
        background: 'rgba(8,12,22,0.42)',
      }}
    >
      <Stack spacing={0.65}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography sx={{ color: '#9fc3de', fontWeight: 950, fontSize: 10.8 }}>
            판단 브리프
          </Typography>
          <JudgmentBadge label={grammar.currentJudgment} />
        </Stack>

        {rows.map(([label, value]) => (
          <Box
            key={label}
            sx={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '110px 1fr',
              gap: 0.45,
              pb: 0.55,
              borderBottom: '1px solid rgba(148,163,184,0.08)',
            }}
          >
            <Typography sx={{ color: UI.sub, fontSize: 10.2, fontWeight: 900 }}>
              {label}
            </Typography>
            <Typography sx={{ color: UI.text, fontSize: 10.9, lineHeight: 1.6 }}>
              {value}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

function HeroDecision({
  hero,
  onPickTag,
  onOpenDetail,
  isMobile,
}: {
  hero: SignalFeed;
  onPickTag: (t: string) => void;
  onOpenDetail: () => void;
  isMobile: boolean;
}) {
  const heat = getImpact(hero);
  const c = heatPalette(heat);
  const tags = parseTags(hero?.issue_tags ?? hero?.issueTags);
  const grammar = buildFeedGrammar(hero);
  const corp = String(hero?.__corpName ?? hero?.__ticker ?? '해당 종목').trim();
  const change = asNumber(hero?.__change ?? 0, 0);
  const close = asNumber(hero?.__close ?? 0, 0);
  const tradingDate = String(hero?.__tradingDate ?? '').trim();
  const tradingValue = asNumber(hero?.__tradingValue ?? 0, 0);
  const title = pickTitle(hero) || '정책 시그널';
  const summary = pickSummary(hero);

  return (
    <Box
      onClick={onOpenDetail}
      sx={{
        p: isMobile ? 1.05 : 1.6,
        borderRadius: 2.8,
        border: `1px solid ${UI.stroke}`,
        background:
          'linear-gradient(135deg, rgba(8,12,22,0.72) 0%, rgba(11,18,34,0.84) 50%, rgba(20,13,18,0.86) 100%)',
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden',
        minWidth: 0,
        boxShadow: UI.shadow,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: -120,
          background: `radial-gradient(circle, ${c.glow} 0%, rgba(0,0,0,0) 60%)`,
          pointerEvents: 'none',
        }}
      />

      <Stack spacing={1} sx={{ position: 'relative', minWidth: 0 }}>
        <Box sx={{ display: 'flex', gap: 0.9, alignItems: 'flex-start', minWidth: 0 }}>
          <RingIcon heat={heat} size={isMobile ? 38 : 50} />

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ color: '#98b6db', fontSize: 10, fontWeight: 950, mb: 0.4 }}>
              PRIMARY POLICY-TO-STOCK SIGNAL
            </Typography>

            <Typography
              sx={{
                color: UI.text,
                fontWeight: 950,
                fontSize: isMobile ? 13 : 16,
                lineHeight: 1.38,
                overflowWrap: 'anywhere',
                wordBreak: 'keep-all',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              오늘 먼저 확인할 정책 이슈: “{title}”
            </Typography>

            <Typography
              sx={{
                color: UI.dim,
                fontSize: isMobile ? 11 : 12.5,
                mt: 0.55,
                lineHeight: 1.55,
                overflowWrap: 'anywhere',
                wordBreak: 'keep-all',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {summary || `${corp} 기준 최신 OHLCV ${fmtDay(tradingDate)}에 이슈가 어떻게 연결되는지 우선 정리합니다.`}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ borderRadius: 2, border: '1px solid rgba(148,163,184,0.14)', background: 'rgba(8,12,22,0.45)', p: 1 }}>
          <Typography sx={{ color: UI.text, fontWeight: 950, fontSize: isMobile ? 11.2 : 12.2, lineHeight: 1.55 }}>
            {corp} 기준 이 이슈는 최신 OHLCV {fmtDay(tradingDate)}에 {directionLabel(change)}으로 연결됩니다.{' '}
            {getImpactInterpretation(heat, change)}
          </Typography>
          <Typography sx={{ color: '#9fc3de', fontWeight: 800, fontSize: 11, lineHeight: 1.6, mt: 0.45 }}>
            종가 {fmtPrice(close)} · 등락률 {fmtPct(change)} · 거래대금 {fmtTradingValue(tradingValue)} · 기준일 {fmtDay(tradingDate)}
          </Typography>
          <Typography sx={{ color: '#ffd58a', fontWeight: 900, fontSize: 11, lineHeight: 1.6, mt: 0.45 }}>
            {getActionTone(change, heat)}
          </Typography>
        </Box>

        <SummaryGrammarMini grammar={grammar} isMobile={isMobile} />

        <Box
          sx={{
            display: 'flex',
            gap: 0.55,
            alignItems: 'center',
            flexWrap: 'nowrap',
            overflowX: 'auto',
            pb: 0.2,
            width: '100%',
            WebkitOverflowScrolling: 'touch',
          }}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        >
          <Chip size="small" label={`HEAT ${Math.round(heat)}`} sx={{ bgcolor: c.chip, border: '1px solid rgba(255,255,255,0.10)', color: '#cfe0ff', fontWeight: 950, flex: '0 0 auto', height: 24 }} />

          {tags.slice(0, isMobile ? 3 : 6).map((t) => (
            <Chip
              key={t}
              size="small"
              label={`#${t}`}
              onClick={() => onPickTag(t)}
              sx={{ bgcolor: 'rgba(148,163,184,0.10)', border: '1px solid rgba(148,163,184,0.16)', color: '#cfe0ff', fontWeight: 900, cursor: 'pointer', flex: '0 0 auto', height: 24 }}
            />
          ))}
        </Box>

        <Button
          variant="contained"
          onClick={(e) => {
            e.stopPropagation();
            onOpenDetail();
          }}
          endIcon={<ArrowForwardRoundedIcon />}
          sx={{
            bgcolor: 'rgba(96,165,250,0.94)',
            color: '#061225',
            fontWeight: 950,
            borderRadius: 2,
            height: 38,
            px: 1.4,
            textTransform: 'none',
            width: '100%',
            minWidth: 0,
            '&:hover': { bgcolor: 'rgba(96,165,250,1)' },
          }}
        >
          상세 판단 보기
        </Button>
      </Stack>
    </Box>
  );
}

function SummaryBanner({
  title,
  conclusion,
  detail,
  tone = 'neutral',
}: {
  title: string;
  conclusion: string;
  detail: string;
  tone?: 'good' | 'bad' | 'neutral';
}) {
  const colors = toneColor(tone);

  return (
    <Box sx={{ p: 1.15, borderRadius: 2, border: `1px solid ${colors.bd}`, background: colors.bg }}>
      <Typography sx={{ color: colors.fg, fontWeight: 950, fontSize: 11.2 }}>
        {title}
      </Typography>
      <Typography sx={{ color: UI.text, fontWeight: 950, fontSize: 12.8, mt: 0.45, lineHeight: 1.55 }}>
        {conclusion}
      </Typography>
      <Typography sx={{ color: '#9fc3de', fontWeight: 800, fontSize: 11, mt: 0.45, lineHeight: 1.6 }}>
        {detail}
      </Typography>
    </Box>
  );
}

function HistoryBriefBanner({
  issues,
  risingCount,
  fallingCount,
  topJudgment,
  latestDate,
}: {
  issues: number;
  risingCount: number;
  fallingCount: number;
  topJudgment: JudgmentLabel;
  latestDate?: string;
}) {
  const tone = judgmentTone(topJudgment);
  const colors = toneColor(tone);

  return (
    <Paper elevation={0} sx={{ p: 1.2, borderRadius: 2.6, border: `1px solid ${colors.bd}`, background: 'rgba(8,12,22,0.58)' }}>
      <Stack spacing={0.8}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} flexWrap="wrap">
          <Typography sx={{ color: UI.text, fontWeight: 950, fontSize: 13.8 }}>
            이슈 히스토리 브리프
          </Typography>
          <JudgmentBadge label={topJudgment} />
        </Stack>

        <Typography sx={{ color: '#cfe0ff', fontWeight: 950, fontSize: 12.4, lineHeight: 1.6 }}>
          현재 화면에서는 총 {issues}건의 이슈 중 최신 OHLCV 기준 상승 반응 관측 {risingCount}건, 하락 반응 관측 {fallingCount}건이 확인됩니다.
        </Typography>

        <Typography sx={{ color: '#9fc3de', fontWeight: 800, fontSize: 11, lineHeight: 1.65 }}>
          가장 많이 나타나는 판단 레이블은{' '}
          <Box component="span" sx={{ color: colors.fg, fontWeight: 950 }}>
            {topJudgment}
          </Box>
          입니다. 최신 반영 기준일은 {fmtDay(latestDate)}이며, 이 페이지는 과거 이슈를 읽는 화면이 아니라 “정책 이슈가 최신 가격·거래대금·종목 반응으로 이어지는지 검증하는 화면”입니다.
        </Typography>
      </Stack>
    </Paper>
  );
}

function SignalRow({
  feed,
  rank,
  onOpen,
  isMobile,
}: {
  feed: SignalFeed;
  rank: number;
  onOpen: () => void;
  isMobile: boolean;
}) {
  const heat = getImpact(feed);
  const c = heatPalette(heat);
  const title = pickTitle(feed) || '(제목 없음)';
  const dateSource = feed?.published_at ?? feed?.publishedAt ?? feed?.date;
  const dateStr = fmtDate(dateSource);
  const ago = fromNowKo(dateSource);
  const tags = parseTags(feed?.issue_tags ?? feed?.issueTags).slice(0, isMobile ? 2 : 3);
  const summary = pickSummary(feed);
  const ticker = String(feed?.__ticker ?? '').trim();
  const corpName = String(feed?.__corpName ?? '').trim();
  const latestChange = asNumber(feed?.__change ?? 0, 0);
  const close = asNumber(feed?.__close ?? 0, 0);
  const volume = asNumber(feed?.__volume ?? 0, 0);
  const tradingValue = asNumber(feed?.__tradingValue ?? 0, 0);
  const tradingDate = String(feed?.__tradingDate ?? '').trim();
  const direction = getDirection(latestChange);
  const grammar = buildFeedGrammar(feed);

  return (
    <Box
      onClick={onOpen}
      sx={{
        p: isMobile ? 0.95 : 1.25,
        borderRadius: 2.35,
        border: `1px solid ${UI.stroke}`,
        background: 'rgba(8,12,22,0.50)',
        cursor: 'pointer',
        minWidth: 0,
        transition: 'transform 0.18s ease, border-color 0.18s ease, background 0.18s ease',
        '&:hover': { transform: 'translateY(-1px)', borderColor: 'rgba(96,165,250,0.28)', background: 'rgba(10,16,28,0.72)' },
      }}
      title="클릭하면 상세 팝업이 열립니다"
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, minWidth: 0 }}>
        <Chip
          size="small"
          label={`#${rank}`}
          sx={{
            bgcolor: rank <= 3 ? 'rgba(96,165,250,.94)' : 'rgba(148,163,184,.14)',
            color: rank <= 3 ? '#061225' : '#dbeafe',
            fontWeight: 1000,
            height: 22,
            flex: '0 0 auto',
          }}
        />
        <RingIcon heat={heat} size={isMobile ? 30 : 40} />

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              color: UI.text,
              fontWeight: 950,
              fontSize: isMobile ? 12.3 : 13.6,
              lineHeight: 1.35,
              overflowWrap: 'anywhere',
              wordBreak: 'keep-all',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {title}
          </Typography>

          <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap', alignItems: 'center', mt: 0.45 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <AccessTimeRoundedIcon sx={{ fontSize: 12, color: UI.sub }} />
              <Typography sx={{ color: UI.sub, fontSize: 10, fontWeight: 800 }}>
                {ago || dateStr || '—'}
              </Typography>
            </Box>

            {corpName && (
              <Typography sx={{ color: '#a8c2e8', fontSize: 10, fontWeight: 900 }}>
                {corpName}
                {ticker ? ` · ${ticker}` : ''}
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 0.6, alignItems: 'center', flexWrap: 'wrap', mt: 0.6 }}>
            <Chip
              size="small"
              label={`HEAT ${Math.round(heat)}`}
              sx={{ bgcolor: c.chip, border: '1px solid rgba(255,255,255,0.10)', color: '#cfe0ff', fontWeight: 950, height: 23, '& .MuiChip-label': { px: 0.8, fontSize: 10 } }}
            />

            <JudgmentBadge label={grammar.currentJudgment} />

            {ticker ? (
              <Chip
                size="small"
                label={`${ticker} ${fmtPct(latestChange)}`}
                sx={microChipSx(direction === 'up' ? 'green' : direction === 'down' ? 'red' : 'slate')}
              />
            ) : null}
          </Box>

          <Box sx={{ mt: 0.65 }}>
            <HeatBar heat={heat} />
          </Box>

          <Box sx={{ mt: 0.75, display: 'grid', gap: 0.35 }}>
            <Typography sx={{ color: UI.sub, fontSize: 10.2, fontWeight: 900 }}>
              무엇이 일어났나
            </Typography>
            <Typography sx={{ color: UI.text, fontWeight: 950, fontSize: isMobile ? 11 : 11.8, lineHeight: 1.55 }}>
              {grammar.whatHappened}
            </Typography>

            <Typography sx={{ color: UI.sub, fontSize: 10.2, fontWeight: 900, mt: 0.3 }}>
              왜 중요한가
            </Typography>
            <Typography sx={{ color: '#9fc3de', fontWeight: 800, fontSize: isMobile ? 10.9 : 11.3, lineHeight: 1.6 }}>
              {grammar.whyImportant}
            </Typography>

            <Typography sx={{ color: UI.sub, fontSize: 10.2, fontWeight: 900, mt: 0.3 }}>
              다음 행동
            </Typography>
            <Typography sx={{ color: '#ffd58a', fontWeight: 900, fontSize: isMobile ? 10.8 : 11.1, lineHeight: 1.55 }}>
              {grammar.nextAction}
            </Typography>
          </Box>

          {summary && (
            <Typography
              sx={{
                mt: 0.65,
                fontSize: isMobile ? 10.8 : 11.5,
                color: 'rgba(210,225,255,0.72)',
                lineHeight: 1.58,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                overflowWrap: 'anywhere',
                wordBreak: 'keep-all',
              }}
            >
              [정책/기사 요약] {summary}
            </Typography>
          )}

          <OhlcvMicroLine
            tradingDate={tradingDate}
            close={close}
            changePct={latestChange}
            volume={volume}
            tradingValue={tradingValue}
          />

          <Typography sx={{ mt: 0.6, color: '#cfe0ff', fontWeight: 900, fontSize: 10.8, lineHeight: 1.55 }}>
            {fmtDay(tradingDate)} 기준 해석: {getImpactInterpretation(heat, latestChange)}
          </Typography>

          {tags.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.75 }}>
              {tags.map((t) => (
                <Chip key={t} size="small" label={`#${t}`} sx={microChipSx('slate')} />
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

function MatchCard({
  item,
  onClick,
}: {
  item: TopMatch;
  onClick: () => void;
}) {
  const direction = getDirection(item.change);

  return (
    <Box
      onClick={onClick}
      sx={{
        p: 0.9,
        borderRadius: 2,
        border: `1px solid ${UI.stroke}`,
        background: 'rgba(8,12,22,0.45)',
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 0.8,
        minWidth: 0,
        transition: 'transform 0.16s ease, border-color 0.16s ease',
        '&:hover': { transform: 'translateY(-1px)', borderColor: 'rgba(96,165,250,0.24)' },
      }}
      title="클릭하면 해당 종목 이슈만 보기"
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ color: UI.text, fontWeight: 950, fontSize: 12 }} noWrap>
          {item.corpName}
        </Typography>
        <Typography sx={{ fontSize: 10.5, color: UI.sub, fontWeight: 800 }} noWrap>
          {item.ticker} · 이슈 {item.newsCount}건 · {fmtDay(item.tradingDate)}
        </Typography>
        <Typography sx={{ fontSize: 10.5, color: '#9fc3de', fontWeight: 800 }} noWrap>
          종가 {fmtPrice(item.close)} · 거래대금 {fmtTradingValue(item.tradingValue)}
        </Typography>
      </Box>

      <Box sx={{ textAlign: 'right', flex: '0 0 auto' }}>
        <Typography
          sx={{
            fontWeight: 1000,
            fontSize: 11.5,
            color: direction === 'up' ? '#b9ffd2' : direction === 'down' ? '#ffd2d2' : UI.dim,
          }}
        >
          {fmtPct(item.change)}
        </Typography>
        <Typography sx={{ color: UI.sub, fontSize: 10, fontWeight: 900 }}>
          {directionLabel(item.change)}
        </Typography>
      </Box>
    </Box>
  );
}

function LoadingPanelSkeleton({ isMobile }: { isMobile: boolean }) {
  return (
    <Box sx={{ display: 'grid', gap: 1.4 }}>
      {Array.from({ length: isMobile ? 3 : 5 }).map((_, idx) => (
        <Box key={idx} sx={{ p: 1.2, borderRadius: 2.6, border: `1px solid ${UI.stroke}`, background: 'rgba(8,12,22,0.45)' }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <Skeleton variant="circular" width={40} height={40} sx={{ bgcolor: 'rgba(148,163,184,0.18)' }} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="30%" height={18} sx={{ bgcolor: 'rgba(148,163,184,0.18)' }} />
              <Skeleton variant="text" width="84%" height={24} sx={{ bgcolor: 'rgba(148,163,184,0.18)' }} />
              <Skeleton variant="text" width="68%" height={18} sx={{ bgcolor: 'rgba(148,163,184,0.18)' }} />
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function EmptyState({ title, desc }: { title: string; desc: string }) {
  return (
    <Box className="pi-panel" sx={{ p: 2.4, textAlign: 'center', background: UI.panel }}>
      <SignalCellularAltRoundedIcon sx={{ color: '#82a7d1', fontSize: 34, mb: 1 }} />
      <Typography sx={{ color: UI.text, fontWeight: 950, fontSize: 16 }}>{title}</Typography>
      <Typography sx={{ color: UI.sub, fontSize: 12.2, mt: 0.75, lineHeight: 1.7 }}>{desc}</Typography>
    </Box>
  );
}

const PolicyInsightDashboard: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true, defaultMatches: false });
  const isLgDown = useMediaQuery(theme.breakpoints.down('lg'), { noSsr: true, defaultMatches: false });

  const {
    state,
    displayList,
    refetch,
    onChangeSector,
    onChangeTag,
    onChangeQuery,
    onChangeTicker,
  } = useDashboardData();

  const [selectedFeed, setSelectedFeed] = useState<any | null>(null);
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const [activeTicker, setActiveTicker] = useState<string | null>(state?.tickerQuery || null);
  const [matchSort, setMatchSort] = useState<'change_desc' | 'change_asc' | 'news_desc'>('change_desc');
  const [fallbackFeeds, setFallbackFeeds] = useState<any[]>([]);
  const [fallbackError, setFallbackError] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  const initialLoading = !state?.hasFetchedOnce && state?.loading && fallbackFeeds.length === 0;
  const refreshing = state?.hasFetchedOnce && state?.loading;

  useEffect(() => {
    let cancelled = false;

    const runFallbackFetch = async () => {
      try {
        setFallbackError('');

        const res = await fetch(`/api/feed/today?_=${Date.now()}`, {
          method: 'GET',
          credentials: 'same-origin',
          cache: 'no-store',
          headers: {
            Accept: 'application/json',
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
          },
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const json = await res.json();
        if (!cancelled && Array.isArray(json)) setFallbackFeeds(json);
      } catch (error: any) {
        if (!cancelled) setFallbackError(error?.message || 'fallback fetch failed');
      }
    };

    runFallbackFetch();

    return () => {
      cancelled = true;
    };
  }, []);

  const rawFeeds = useMemo(() => {
    const fromState = getRawFeedsFromState(state);
    if (fromState.length > 0) return fromState;

    return fallbackFeeds;
  }, [state, fallbackFeeds]);

  const normalizedDisplayList = useMemo(() => {
    return normalizeDashboardDisplayRows((displayList ?? []) as any[], rawFeeds, state?.sector);
  }, [displayList, rawFeeds, state?.sector]);


  const sectorStats = useMemo(() => buildSectorStats(rawFeeds), [rawFeeds]);
  const activeSectorValue = useMemo(() => normalizeDashboardSector(state?.sector), [state?.sector]);

  const handleSectorChange = useCallback(
    (nextSector: Parameters<typeof onChangeSector>[0]) => {
      setActiveTag(null);
      setActiveTicker(null);
      setSelectedFeed(null);
      onChangeTag?.('');
      onChangeQuery?.('');
      onChangeTicker?.('');
      onChangeSector(nextSector);
    },
    [onChangeSector, onChangeTag, onChangeQuery, onChangeTicker],
  );

  const pickTag = useCallback(
    (tag: string) => {
      const clean = tag.replace(/^#/, '').trim();
      if (!clean) return;

      setActiveTicker(null);
      setActiveTag(clean);
      onChangeTicker?.('');
      onChangeTag?.(clean);
      onChangeQuery?.('');
    },
    [onChangeTag, onChangeQuery, onChangeTicker],
  );

  const pickTicker = useCallback(
    (ticker: string) => {
      const t = (ticker ?? '').trim();
      if (!t) return;

      setActiveTag(null);
      setActiveTicker(t);
      onChangeTag?.('');
      onChangeQuery?.('');
      onChangeTicker?.(t);
    },
    [onChangeTag, onChangeQuery, onChangeTicker],
  );

  const clearFilters = useCallback(() => {
    setActiveTag(null);
    setActiveTicker(null);
    onChangeTag?.('');
    onChangeQuery?.('');
    onChangeTicker?.('');
  }, [onChangeTag, onChangeQuery, onChangeTicker]);

  const openDetail = useCallback((feed: any) => {
    if (!feed) return;

    const id = String(feed.feed_id ?? feed.feedId ?? feed.id ?? '');

    setSelectedFeed({
      ...feed,
      id,
      feed_id: id,
    });
  }, []);

  const closeDetail = useCallback(() => setSelectedFeed(null), []);

  const filteredDisplayList = useMemo(() => {
    let out = (normalizedDisplayList ?? []) as StockChange[];

    if (activeTicker) {
      out = out.filter((r) => String((r as any).ticker ?? '').trim() === String(activeTicker).trim());
    }

    if (activeTag) {
      out = out
        .map((r) => {
          const list = ((r as any).newsList ?? []) as any[];
          const keep = list.filter((f) => feedHasTag(f, activeTag));

          return {
            ...r,
            newsList: keep,
          };
        })
        .filter((r) => ((r as any).newsList?.length ?? 0) > 0);
    }

    return out;
  }, [normalizedDisplayList, activeTicker, activeTag]);

  const allFeeds = useMemo<SignalFeed[]>(() => {
    const flat: SignalFeed[] = [];

    for (const row of filteredDisplayList as any[]) {
      const news = (row.newsList ?? []) as any[];
      const rowChange = getChangePct(row);
      const rowClose = getClosePrice(row);
      const rowOpen = getOpenPrice(row);
      const rowHigh = getHighPrice(row);
      const rowLow = getLowPrice(row);
      const rowVolume = getVolume(row);
      const rowTradingValue = getTradingValue(row);
      const rowDate = getRowDate(row);

      for (const f of news) {
        flat.push({
          ...f,
          __ticker: row.ticker,
          __corpName: row.corpName,
          __change: rowChange,
          __close: rowClose,
          __open: rowOpen,
          __high: rowHigh,
          __low: rowLow,
          __volume: rowVolume,
          __tradingValue: rowTradingValue,
          __tradingDate: rowDate,
        });
      }
    }

    const uniq = uniqByFeedId(flat);

    uniq.sort((a, b) => {
      const ib = getImpact(b);
      const ia = getImpact(a);
      if (ib !== ia) return ib - ia;

      const absB = Math.abs(asNumber(b.__change ?? 0, 0));
      const absA = Math.abs(asNumber(a.__change ?? 0, 0));
      if (absB !== absA) return absB - absA;

      const tb = dayjs(b?.published_at ?? b?.publishedAt ?? b?.date ?? 0).valueOf();
      const ta = dayjs(a?.published_at ?? a?.publishedAt ?? a?.date ?? 0).valueOf();

      return tb - ta;
    });

    return uniq;
  }, [filteredDisplayList]);

  const visibleSignals = useMemo(() => allFeeds.slice(0, isMobile ? 12 : 20), [allFeeds, isMobile]);
  const hero = useMemo(() => allFeeds[0] ?? null, [allFeeds]);

  const topMatches = useMemo<TopMatch[]>(() => {
    const rows = (filteredDisplayList ?? []) as any[];

    const mapped = rows
      .filter((row) => row?.ticker && row.ticker !== '__POLICY__')
      .map((row) => {
        const news = (row.newsList ?? []) as any[];
        const impactAvg = news.length ? news.reduce((sum: number, f: any) => sum + getImpact(f), 0) / news.length : 0;

        return {
          ticker: String(row.ticker ?? ''),
          corpName: String(row.corpName ?? row.name ?? row.ticker ?? ''),
          newsCount: news.length,
          change: getChangePct(row),
          close: getClosePrice(row),
          tradingValue: getTradingValue(row),
          tradingDate: getRowDate(row),
          impactAvg,
        };
      });

    mapped.sort((a, b) => {
      if (matchSort === 'news_desc') {
        if (b.newsCount !== a.newsCount) return b.newsCount - a.newsCount;
        return b.impactAvg - a.impactAvg;
      }

      if (matchSort === 'change_asc') return a.change - b.change;

      return b.change - a.change;
    });

    return mapped.slice(0, isMobile ? 5 : 12);
  }, [filteredDisplayList, matchSort, isMobile]);

  const pageSummary = useMemo(() => {
    const issues = allFeeds.length;
    const appliedStocks = (filteredDisplayList ?? []).filter((x: any) => x?.ticker && x.ticker !== '__POLICY__').length;
    const rising = (filteredDisplayList ?? []).filter((x: any) => getChangePct(x) > 0);
    const falling = (filteredDisplayList ?? []).filter((x: any) => getChangePct(x) < 0);
    const flat = Math.max(0, appliedStocks - rising.length - falling.length);

    const avgAbsMove = appliedStocks
      ? (filteredDisplayList ?? []).reduce((sum: number, row: any) => sum + Math.abs(getChangePct(row)), 0) / Math.max(1, appliedStocks)
      : 0;

    const latestDate =
      (filteredDisplayList ?? [])
        .map((x: any) => getRowDate(x))
        .filter(Boolean)
        .sort((a: string, b: string) => dayjs(b).valueOf() - dayjs(a).valueOf())[0] || '';

    const counts: Record<string, number> = {};
    allFeeds.forEach((feed) => {
      const j = buildJudgment(feed);
      counts[j] = (counts[j] || 0) + 1;
    });

    const topJudgment = (Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] || '수급 확인') as JudgmentLabel;

    const strongestUp = [...(filteredDisplayList ?? [])].sort((a: any, b: any) => getChangePct(b) - getChangePct(a))[0] as any;
    const strongestDown = [...(filteredDisplayList ?? [])].sort((a: any, b: any) => getChangePct(a) - getChangePct(b))[0] as any;

    const conclusion =
      falling.length > rising.length
        ? '하락 반응 관측 종목이 더 많아 보수적인 확인이 필요합니다.'
        : rising.length > falling.length
          ? '상승 반응 관측 종목이 더 많지만 선별 확인이 우선입니다.'
          : '상승·하락 반응이 엇갈려 종목별 검증이 필요한 구간입니다.';

    const detail = `평균 절대 등락폭은 ${fmtPct(avgAbsMove, 2)}이며, 최신 반영 기준일은 ${fmtDay(latestDate)}입니다.`;

    const tone: 'good' | 'bad' | 'neutral' = rising.length > falling.length ? 'good' : falling.length > rising.length ? 'bad' : 'neutral';

    return {
      issues,
      appliedStocks,
      risingCount: rising.length,
      fallingCount: falling.length,
      flatCount: flat,
      avgAbsMove,
      latestDate,
      topJudgment,
      strongestUp: strongestUp
        ? {
            corpName: strongestUp.corpName,
            ticker: strongestUp.ticker,
            change: getChangePct(strongestUp),
          }
        : null,
      strongestDown: strongestDown
        ? {
            corpName: strongestDown.corpName,
            ticker: strongestDown.ticker,
            change: getChangePct(strongestDown),
          }
        : null,
      conclusion,
      detail,
      tone,
    };
  }, [filteredDisplayList, allFeeds]);

  const stickyTopOffset = NAV_H + 16;

  const MatchSortChips = (
    <Box sx={{ display: 'flex', gap: 0.55, flexWrap: 'wrap' }}>
      <Chip size="small" label="▲ 상승률" onClick={() => setMatchSort('change_desc')} sx={sortChipSx(matchSort === 'change_desc')} />
      <Chip size="small" label="▼ 하락률" onClick={() => setMatchSort('change_asc')} sx={sortChipSx(matchSort === 'change_asc')} />
      <Chip size="small" label="이슈 수" onClick={() => setMatchSort('news_desc')} sx={sortChipSx(matchSort === 'news_desc')} />
    </Box>
  );

  const showEmpty = !initialLoading && visibleSignals.length === 0;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        pt: { xs: 1, md: 2 },
        pb: 4,
        background:
          'radial-gradient(circle at 6% 0%, rgba(103,232,249,0.08), transparent 28%), radial-gradient(circle at 92% 0%, rgba(167,139,250,0.10), transparent 28%), linear-gradient(180deg, #050914 0%, #08111f 44%, #050914 100%)',
      }}
    >
      <GlobalStyles
        styles={{
          body: {
            backgroundColor: UI.bg,
          },
          '.pi-panel': {
            border: `1px solid ${UI.stroke}`,
            background: UI.panel,
            borderRadius: 18,
            boxShadow: '0 18px 48px rgba(0,0,0,0.24)',
            backdropFilter: 'blur(16px)',
          },
          '.pi-scrollbar': {
            scrollbarWidth: 'thin',
          },
          '.pi-scrollbar::-webkit-scrollbar': {
            width: 8,
            height: 8,
          },
          '.pi-scrollbar::-webkit-scrollbar-thumb': {
            background: 'rgba(148,163,184,0.28)',
            borderRadius: 999,
          },
        }}
      />

      {refreshing ? (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 2000,
            height: 3,
            bgcolor: 'rgba(96,165,250,0.08)',
            '& .MuiLinearProgress-bar': { bgcolor: UI.cyan },
          }}
        />
      ) : null}

      <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 1.5, md: 2 } }}>
        <Box sx={{ display: 'grid', gap: isMobile ? 1 : 1.35 }}>
          <Paper
            elevation={0}
            className="pi-panel"
            sx={{
              p: isMobile ? 1.15 : 1.6,
              overflow: 'hidden',
              background:
                'radial-gradient(circle at 0% 0%, rgba(103,232,249,0.12), transparent 30%), radial-gradient(circle at 100% 0%, rgba(167,139,250,0.12), transparent 30%), rgba(10,16,28,0.76)',
            }}
          >
            <Stack spacing={1.15}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1} flexWrap="wrap">
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={0.65} useFlexGap flexWrap="wrap" sx={{ mb: 0.8 }}>
                    <Chip size="small" icon={<BoltRoundedIcon sx={{ fontSize: 15, color: UI.cyan }} />} label="PolicyIntel" sx={headerChipSx('cyan')} />
                    <StatusPill loading={!!state?.loading} />
                    {fallbackError ? (
                      <Chip size="small" label={`fallback ${fallbackError}`} sx={headerChipSx('red')} />
                    ) : null}
                  </Stack>

                  <Typography
                    sx={{
                      color: UI.text,
                      fontSize: isMobile ? 23 : 34,
                      fontWeight: 950,
                      letterSpacing: '-0.045em',
                      lineHeight: 1.16,
                    }}
                  >
                    정책 이슈가 종목 반응으로
                    <br />
                    전이되는 순간을 검증합니다
                  </Typography>

                  <Typography sx={{ color: UI.dim, fontSize: isMobile ? 12.2 : 14, lineHeight: 1.75, mt: 1, maxWidth: 980 }}>
                    해외 정책·이슈·논문을 단순 요약하지 않고, 최신 OHLCV·거래대금·종목 반응과 연결해 사용자가 “왜 봐야 하는지”를 먼저 판단하게 합니다.
                  </Typography>
                </Box>

                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ justifyContent: 'flex-end' }}>
                  <Tooltip title="데이터 새로고침">
                    <IconButton
                      onClick={() => refetch?.()}
                      sx={{
                        width: 40,
                        height: 40,
                        border: `1px solid ${UI.stroke}`,
                        bgcolor: UI.glass,
                        color: UI.text,
                        '&:hover': { bgcolor: 'rgba(96,165,250,0.12)' },
                      }}
                    >
                      <RefreshRoundedIcon />
                    </IconButton>
                  </Tooltip>

                  <Button
                    variant="outlined"
                    startIcon={<FilterAltRoundedIcon />}
                    onClick={() => setFilterOpen((prev) => !prev)}
                    sx={{
                      borderColor: 'rgba(148,163,184,0.22)',
                      color: UI.text,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 900,
                    }}
                  >
                    필터
                  </Button>
                </Stack>
              </Stack>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0,1fr))', md: 'repeat(4, minmax(0,1fr))' }, gap: 0.85 }}>
                <MiniKpi icon={<InsightsIcon />} title="정책 이슈" value={`${pageSummary.issues.toLocaleString()}건`} sub="현재 필터 기준" isMobile={isMobile} />
                <MiniKpi icon={<CandlestickChartRoundedIcon fontSize="small" />} title="연결 종목" value={`${pageSummary.appliedStocks.toLocaleString()}개`} sub="OHLCV 매핑" isMobile={isMobile} />
                <MiniKpi icon={<PriceCheckRoundedIcon fontSize="small" />} title="상승/하락" value={`${pageSummary.risingCount}/${pageSummary.fallingCount}`} sub={`중립 ${pageSummary.flatCount}`} isMobile={isMobile} />
                <MiniKpi icon={<VerifiedRoundedIcon fontSize="small" />} title="대표 판단" value={pageSummary.topJudgment} sub={`기준 ${fmtDay(pageSummary.latestDate)}`} isMobile={isMobile} />
              </Box>
            </Stack>
          </Paper>

          <Paper elevation={0} className="pi-panel" sx={{ p: isMobile ? 0.8 : 1.1 }}>
            <Stack spacing={1}>
              <DashboardSectorTabs
                value={activeSectorValue}
                stats={sectorStats}
                isMobile={isMobile}
                onChange={(nextSector) => handleSectorChange(nextSector as Parameters<typeof onChangeSector>[0])}
              />

              {(filterOpen || activeTag || activeTicker) && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.7,
                    flexWrap: 'wrap',
                    borderTop: '1px solid rgba(148,163,184,0.10)',
                    pt: 1,
                  }}
                >
                  {activeTag ? <Chip size="small" label={`태그 #${activeTag}`} onDelete={() => pickTag('')} sx={headerChipSx('violet')} /> : null}
                  {activeTicker ? <Chip size="small" label={`종목 ${activeTicker}`} onDelete={() => pickTicker('')} sx={headerChipSx('cyan')} /> : null}
                  {(activeTag || activeTicker) ? (
                    <Button size="small" startIcon={<CloseRoundedIcon />} onClick={clearFilters} sx={{ color: UI.sub, fontWeight: 900, textTransform: 'none' }}>
                      필터 초기화
                    </Button>
                  ) : (
                    <Typography sx={{ color: UI.sub, fontSize: 12, fontWeight: 800 }}>
                      섹터를 바꾸면 이슈와 연결 종목이 함께 재정렬됩니다.
                    </Typography>
                  )}
                </Box>
              )}
            </Stack>
          </Paper>

          {initialLoading ? (
            <LoadingPanelSkeleton isMobile={isMobile} />
          ) : showEmpty ? (
            <EmptyState title="표시할 정책 전이 데이터가 없습니다" desc="섹터·태그·종목 필터를 조정하거나 새로고침을 눌러 최신 데이터를 다시 불러오세요." />
          ) : (
            <Box
              sx={{
                display: 'grid',
                gap: isMobile ? 1 : 1.35,
                gridTemplateColumns: isLgDown ? '1fr' : 'minmax(0,1fr) 360px',
                alignItems: 'start',
              }}
            >
              <Box sx={{ display: 'grid', gap: isMobile ? 1 : 1.35 }}>
                <HistoryBriefBanner
                  issues={pageSummary.issues}
                  risingCount={pageSummary.risingCount}
                  fallingCount={pageSummary.fallingCount}
                  topJudgment={pageSummary.topJudgment}
                  latestDate={pageSummary.latestDate}
                />

                <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: isMobile ? '1fr' : 'minmax(0,1.22fr) minmax(0,0.78fr)' }}>
                  {hero && (
                    <HeroDecision
                      hero={hero}
                      onPickTag={pickTag}
                      onOpenDetail={() => openDetail(hero)}
                      isMobile={isMobile}
                    />
                  )}

                  <Box className="pi-panel" sx={{ p: isMobile ? 1 : 1.25, display: 'grid', gap: 1 }}>
                    <SummaryBanner
                      title="전체 해석"
                      conclusion={pageSummary.conclusion}
                      detail={pageSummary.detail}
                      tone={pageSummary.tone}
                    />
                    <SummaryBanner
                      title="상승 반응 관측 상위"
                      conclusion={
                        pageSummary.strongestUp
                          ? `${pageSummary.strongestUp.corpName} (${pageSummary.strongestUp.ticker}) ${fmtPct(pageSummary.strongestUp.change)}`
                          : '상승 반응 관측 종목 없음'
                      }
                      detail="최신 OHLCV 기준 상대적으로 큰 상승 반응이 함께 관측된 종목입니다."
                      tone="good"
                    />
                    <SummaryBanner
                      title="하락 반응 관측 상위"
                      conclusion={
                        pageSummary.strongestDown
                          ? `${pageSummary.strongestDown.corpName} (${pageSummary.strongestDown.ticker}) ${fmtPct(pageSummary.strongestDown.change)}`
                          : '하락 반응 관측 종목 없음'
                      }
                      detail="최신 OHLCV 기준 상대적으로 큰 하락 반응이 함께 관측된 종목입니다."
                      tone="bad"
                    />
                  </Box>
                </Box>

                <Box className="pi-panel" sx={{ p: isMobile ? 0.9 : 1.25 }}>
                  <SectionHeader
                    title="정책 전이 시그널"
                    sub="뉴스를 최신 가격·거래대금·종목 반응에 연결해 우선순위를 재정렬합니다."
                    isMobile={isMobile}
                    right={
                      <Chip
                        size="small"
                        icon={<BoltRoundedIcon sx={{ fontSize: 16, color: '#cfe0ff' }} />}
                        label="클릭 → 상세 팝업"
                        sx={headerChipSx('blue')}
                      />
                    }
                  />
                  <Divider sx={{ my: 1, borderColor: 'rgba(148,163,184,0.10)' }} />

                  <Box sx={{ display: 'grid', gap: isMobile ? 0.75 : 1 }}>
                    {visibleSignals.map((feed: SignalFeed, index: number) => (
                      <SignalRow
                        key={String(feed.feed_id ?? feed.feedId ?? feed.id ?? `${feed.__ticker}_${index}`)}
                        feed={feed}
                        rank={index + 1}
                        isMobile={isMobile}
                        onOpen={() => openDetail(feed)}
                      />
                    ))}
                  </Box>
                </Box>
              </Box>

              {!isLgDown && (
                <Box
                  className="pi-panel"
                  sx={{
                    p: 1.2,
                    position: 'sticky',
                    top: stickyTopOffset,
                    alignSelf: 'start',
                  }}
                >
                  <SectionHeader title="Top Match" sub="종목코드 기준으로 정확히 필터링합니다." right={MatchSortChips} />
                  <Divider sx={{ my: 1, borderColor: 'rgba(148,163,184,0.10)' }} />

                  {topMatches.length > 0 ? (
                    <Box sx={{ display: 'grid', gap: 0.75 }}>
                      {topMatches.map((item) => (
                        <MatchCard key={item.ticker} item={item} onClick={() => pickTicker(item.ticker)} />
                      ))}
                    </Box>
                  ) : (
                    <Typography sx={{ color: UI.sub, fontSize: 12 }}>조건에 맞는 종목이 없습니다.</Typography>
                  )}
                </Box>
              )}

              {isLgDown && topMatches.length > 0 ? (
                <Box className="pi-panel" sx={{ p: 1 }}>
                  <SectionHeader title="Top Match" sub="종목별 이슈 묶음입니다." right={MatchSortChips} isMobile={isMobile} />
                  <Divider sx={{ my: 1, borderColor: 'rgba(148,163,184,0.10)' }} />
                  <Box sx={{ display: 'grid', gap: 0.75, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0,1fr))' } }}>
                    {topMatches.map((item) => (
                      <MatchCard key={item.ticker} item={item} onClick={() => pickTicker(item.ticker)} />
                    ))}
                  </Box>
                </Box>
              ) : null}
            </Box>
          )}
        </Box>
      </Container>

      <FeedDetailDialog open={!!selectedFeed} feed={selectedFeed} onClose={closeDetail} />
    </Box>
  );
};

function InsightsIcon() {
  return <SignalCellularAltRoundedIcon fontSize="small" />;
}

function headerChipSx(tone: 'cyan' | 'green' | 'blue' | 'violet' | 'red' | 'slate') {
  const map = {
    cyan: { bg: 'rgba(103,232,249,.12)', bd: 'rgba(103,232,249,.24)', fg: '#cffafe' },
    green: { bg: 'rgba(52,211,153,.12)', bd: 'rgba(52,211,153,.22)', fg: '#b9ffd2' },
    blue: { bg: 'rgba(96,165,250,.12)', bd: 'rgba(96,165,250,.22)', fg: '#cfe0ff' },
    violet: { bg: 'rgba(167,139,250,.12)', bd: 'rgba(167,139,250,.22)', fg: '#e9d5ff' },
    red: { bg: 'rgba(251,113,133,.12)', bd: 'rgba(251,113,133,.22)', fg: '#ffd2dc' },
    slate: { bg: 'rgba(148,163,184,.10)', bd: 'rgba(148,163,184,.16)', fg: '#dcecff' },
  }[tone];

  return {
    bgcolor: map.bg,
    border: `1px solid ${map.bd}`,
    color: map.fg,
    fontWeight: 950,
    height: 28,
    maxWidth: '100%',
  };
}

function sortChipSx(active: boolean) {
  return {
    bgcolor: active ? 'rgba(96,165,250,0.16)' : 'rgba(148,163,184,0.08)',
    border: active ? '1px solid rgba(96,165,250,0.28)' : '1px solid rgba(148,163,184,0.16)',
    color: active ? '#dbeafe' : UI.sub,
    fontWeight: 900,
    cursor: 'pointer',
  };
}

export default PolicyInsightDashboard;
